import { Box, Grid, Typography, useTheme } from '@mui/joy';
import { UseFormReset, UseFormSetValue } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import { Form, FormField } from 'core/components';
import { FlexBox } from 'core/elements';
import { InputType } from 'core/enums';
import { useFormContext } from 'core/states';
import { PricingHelper } from 'pricing/helpers';
import { useCostDecimals } from 'pricing/hooks';
import { CostCard, PriceCard, PriceLockTooltip } from 'quotes/elements';
import { QuotePartHelper } from 'quotes/helpers';
import { ICalculatePnbPriceForm, ICustomerQuotePart } from 'quotes/types';
import { Size } from 'theme/enums';

import { PricingGroup } from './PricingGroup';
import { IBaseAdderMarginChangeValues, IBaseAdderPurchaseCostChangeValues } from './types';
import { getFormGroupFieldPricing } from './utils';

interface IProps {
  isOpen: boolean;
  onClose: (reset?: UseFormReset<ICalculatePnbPriceForm>, closeSelection?: boolean) => void;
  onSubmit: (
    reset?: UseFormReset<ICalculatePnbPriceForm>
  ) => (data: ICalculatePnbPriceForm) => void;
  selectedQuotePart: ICustomerQuotePart;
  onPurchaseCostChange: (
    purchaseCost: number,
    price: number,
    reset: UseFormReset<ICalculatePnbPriceForm>
  ) => void;
  onBaseAdderPurchaseCostChange: (
    values: IBaseAdderPurchaseCostChangeValues,
    setValue: UseFormSetValue<ICalculatePnbPriceForm>,
    type: string
  ) => void;
  onPriceChange: (
    price: number,
    cost: number,
    setValue: UseFormSetValue<ICalculatePnbPriceForm>,
    type?: string
  ) => void;
  onMarginChange: (
    margin: number,
    cost: number,
    costBase: number,
    costAdder: number,
    setValue: UseFormSetValue<ICalculatePnbPriceForm>
  ) => void;
  onBaseAdderMarginChange: (
    values: IBaseAdderMarginChangeValues,
    setValue: UseFormSetValue<ICalculatePnbPriceForm>,
    type: string
  ) => void;
  interactive?: boolean;
}

export const QuotePartUpdatePnbMarginFormView = ({
  isOpen = false,
  onClose,
  onSubmit,
  selectedQuotePart,
  onPurchaseCostChange,
  onBaseAdderPurchaseCostChange,
  onPriceChange,
  onMarginChange,
  onBaseAdderMarginChange,
  interactive = true,
}: IProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const costDecimals = useCostDecimals();

  const { control, errors, watch, setValue, reset } = useFormContext();

  const isPriceLocked = QuotePartHelper.isPriceLocked(selectedQuotePart);

  const subtitleValueWrapper = (
    <Typography fontWeight={800} sx={{ color: theme.palette.neutral[800] }} />
  );

  const subtitle = (
    <Trans
      i18nKey="subtitleForSelectedPart"
      values={{
        partNumber: selectedQuotePart?.partArr?.number,
        count: 1,
      }}
      components={{
        wrapper: <Box sx={{ display: 'flex', gap: 0.5 }} />,
        valueWrapper: subtitleValueWrapper,
      }}
    />
  );

  const priceValue = watch('price');
  const priceBaseValue = watch('priceBase');
  const priceAdderValue = watch('priceAdder');

  const marginBaseValue = watch('marginBase');
  const marginAdderValue = watch('marginAdder');

  const computedCostValue = watch('computedCost');
  const computedCostBaseValue = watch('computedCostBase');
  const computedCostAdderValue = watch('computedCostAdder');

  const computedPurchaseCostBaseValue = watch('computedPurchaseCostBase');
  const computedPurchaseCostAdderValue = watch('computedPurchaseCostAdder');

  const disableApplyBaseAndAdderToAllSameBase =
    (!Number(computedCostBaseValue) || !Number(computedCostAdderValue)) &&
    (!Number(marginBaseValue) || !Number(marginAdderValue)) &&
    (!Number(priceBaseValue) || !Number(priceAdderValue));

  return (
    <Form
      title={t('titleCalculateConfiguredPricing')}
      subtitle={subtitle}
      isOpen={isOpen}
      onClose={onClose}
      submitBtnLabel={t('buttonLabelUpdatePricing')}
      onSubmit={onSubmit}
      interactive={interactive}
    >
      {!!selectedQuotePart && (
        <>
          <Grid container spacing={2}>
            <Grid xs={12} md={6}>
              <CostCard
                key={computedCostValue}
                quotePart={{ ...selectedQuotePart, computedCost: computedCostValue }}
                label={t('labelTotalUnitStandardCost')}
                displayPurchaseCost={false}
                sx={{ height: '100%' }}
              />
            </Grid>
            <Grid xs={12} md={6}>
              <PriceCard
                cost={computedCostValue}
                label={
                  <FlexBox>
                    {t('labelTotalUnitPrice')} <PriceLockTooltip quotePart={selectedQuotePart} />
                  </FlexBox>
                }
                values={{
                  decimals: selectedQuotePart?.quotePriceDecimals,
                  onPriceChange: (value) => onPriceChange(value, computedCostValue, setValue),
                  control,
                  error: errors.price,
                  isPriceLocked,
                }}
              />
            </Grid>
          </Grid>

          <PricingGroup
            cost={computedCostBaseValue}
            onPriceChange={onPriceChange}
            decimals={selectedQuotePart?.quotePriceDecimals}
            type={PricingHelper.PRICING_TYPE_BASE}
            containerStyle={{ pt: 1 }}
            disabled={isPriceLocked}
          />

          <PricingGroup
            cost={computedCostAdderValue}
            onPriceChange={onPriceChange}
            decimals={selectedQuotePart?.quotePriceDecimals}
            type={PricingHelper.PRICING_TYPE_ADDER}
            disabled={isPriceLocked}
          />

          {getFormGroupFieldPricing({
            name: 'pricingUnit',
            quotePart: selectedQuotePart,
            type: PricingHelper.PRICING_TYPE_TOTAL,
            onPurchaseCostChange: ({ target: { value }, source }) => {
              if (source !== 'event') {
                return;
              }

              onPurchaseCostChange(Number(value), priceValue, reset);
            },
            onMarginChange: ({ target: { value }, source }) => {
              if (source !== 'event') {
                return;
              }

              onMarginChange(
                Number(value),
                computedCostValue,
                computedCostBaseValue,
                computedCostAdderValue,
                setValue
              );
            },
            disabled: selectedQuotePart?.partArr?.wildcard,
            isPriceLocked,
            isMarginDisabled: !(computedCostValue > 0),
            costDecimals,
          })}

          {getFormGroupFieldPricing({
            name: 'pricingBase',
            quotePart: selectedQuotePart,
            type: PricingHelper.PRICING_TYPE_BASE,
            onPurchaseCostChange: ({ target: { value }, source }) => {
              if (source !== 'event') {
                return;
              }

              onBaseAdderPurchaseCostChange(
                {
                  purchaseCostBase: Number(value),
                  purchaseCostAdder: computedPurchaseCostAdderValue,
                  costBase: computedCostBaseValue,
                  costAdder: computedCostAdderValue,
                  priceBase: priceBaseValue,
                  priceAdder: priceAdderValue,
                },
                setValue,
                PricingHelper.PRICING_TYPE_BASE
              );
            },
            onMarginChange: ({ target: { value }, source }) => {
              if (source !== 'event') {
                return;
              }

              onBaseAdderMarginChange(
                {
                  marginBase: Number(value),
                  marginAdder: marginAdderValue,
                  costBase: computedCostBaseValue,
                  costAdder: computedCostAdderValue,
                  priceBase: priceBaseValue,
                  priceAdder: priceAdderValue,
                  cost: computedCostValue,
                },
                setValue,
                PricingHelper.PRICING_TYPE_BASE
              );
            },
            isPriceLocked,
            isMarginDisabled: !(computedPurchaseCostBaseValue > 0),
            costDecimals,
          })}

          {getFormGroupFieldPricing({
            name: 'pricingAdder',
            quotePart: selectedQuotePart,
            type: PricingHelper.PRICING_TYPE_ADDER,
            onPurchaseCostChange: ({ target: { value }, source }) => {
              if (source !== 'event') {
                return;
              }

              onBaseAdderPurchaseCostChange(
                {
                  purchaseCostBase: computedPurchaseCostBaseValue,
                  purchaseCostAdder: Number(value),
                  costBase: computedCostBaseValue,
                  costAdder: computedCostAdderValue,
                  priceBase: priceBaseValue,
                  priceAdder: priceAdderValue,
                },
                setValue,
                PricingHelper.PRICING_TYPE_ADDER
              );
            },
            onMarginChange: ({ target: { value }, source }) => {
              if (source !== 'event') {
                return;
              }
              onBaseAdderMarginChange(
                {
                  marginBase: marginBaseValue,
                  marginAdder: Number(value),
                  costBase: computedCostBaseValue,
                  costAdder: computedCostAdderValue,
                  priceBase: priceBaseValue,
                  priceAdder: priceAdderValue,
                  cost: computedCostValue,
                },
                setValue,
                PricingHelper.PRICING_TYPE_ADDER
              );
            },
            isPriceLocked,
            isMarginDisabled: !(computedPurchaseCostAdderValue > 0),
            costDecimals,
          })}

          <FormField
            name="allSameBase"
            type={InputType.CHECKBOX}
            inputProps={{
              size: Size.MD,
              label: t('labelApplyBaseAndAdderToLinesWithSameBase'),
              disabled: disableApplyBaseAndAdderToAllSameBase,
              sx: { paddingTop: '16px' },
            }}
          />
        </>
      )}
    </Form>
  );
};
